import type { ReactNode } from 'react';

import { Skeleton } from '../../../components/ui';
import { cn } from '../../../lib/cn';
import { soVN } from '../../../lib/dinh-dang';
import { EmptyHint, Tile } from '../../dashboard/components/primitives';
import { phanTramThanh } from '../nghiep-vu/tong-hop';
import type { BarEntry } from '../types';

/** Tông ô biểu tượng của panel; chỉ bậc ngữ nghĩa của token. */
export const TONG_PANEL = {
  teal: 'bg-teal-50 text-teal-600',
  violet: 'bg-violet-50 text-violet-600',
  sky: 'bg-sky-50 text-sky-600',
  warning: 'bg-warning-50 text-warning-600',
} as const;

export interface SoLieu {
  nhan: string;
  value: number;
}

/** Một panel: header (ô biểu tượng + tiêu đề), lưới 4 ô số, rồi biểu đồ. */
export function Panel({
  icon,
  tong,
  tieuDe,
  oSo,
  tieuDePhu,
  children,
  loading,
  locale,
}: {
  icon: ReactNode;
  tong: keyof typeof TONG_PANEL;
  tieuDe: string;
  oSo: SoLieu[];
  tieuDePhu: string;
  children: ReactNode;
  loading: boolean;
  locale: string;
}) {
  return (
    <Tile className="col-span-12 lg:col-span-6">
      <div className="flex items-center gap-3 border-b border-default px-5 py-3">
        <span className={cn('grid h-8 w-8 shrink-0 place-items-center rounded', TONG_PANEL[tong])}>
          {icon}
        </span>
        <h2 className="min-w-0 truncate text-sm font-semibold text-content-primary">{tieuDe}</h2>
      </div>
      <div className="space-y-5 p-5">
        <dl className="grid grid-cols-2 divide-x divide-y divide-[var(--border-default)] overflow-hidden rounded-lg border border-default">
          {oSo.map((o, i) => (
            <div
              key={o.nhan}
              className={cn('px-3 py-4 text-center', i < 2 && 'border-t-0', i % 2 === 0 && 'border-l-0')}
            >
              <dd className="text-3xl font-bold leading-none tabular-nums text-content-primary">
                {loading ? '—' : soVN(o.value, locale)}
              </dd>
              <dt className="mt-1.5 text-xs text-content-muted">{o.nhan}</dt>
            </div>
          ))}
        </dl>
        <div>
          <h3 className="mb-3 text-xs font-semibold text-content-secondary">{tieuDePhu}</h3>
          {children}
        </div>
      </div>
    </Tile>
  );
}

/** Danh sách thanh ngang. Rỗng thì hiện lời nhắn; đang tải thì Skeleton cao 96px. */
export function DanhSachThanh({
  muc,
  max,
  rong,
  loading,
  locale,
}: {
  muc: BarEntry[];
  /** Mặc định: giá trị lớn nhất trong danh sách, tối thiểu 1. */
  max?: number;
  rong: string;
  loading: boolean;
  locale: string;
}) {
  if (loading) return <Skeleton className="h-24 w-full" />;
  if (muc.length === 0) return <EmptyHint>{rong}</EmptyHint>;
  const tran = max ?? Math.max(1, ...muc.map((m) => m.value));
  return (
    <ul className="space-y-2">
      {muc.map((m) => (
        <li key={m.nhan} className="flex items-center gap-3">
          <span className="w-40 shrink-0 truncate text-xs text-content-secondary" title={m.nhan}>
            {m.nhan}
          </span>
          <div className="h-4 min-w-0 flex-1 overflow-hidden rounded-full bg-subtle">
            <div
              className={cn('h-full rounded-full transition-all', m.mau)}
              style={{ width: `${phanTramThanh(m.value, tran)}%` }}
            />
          </div>
          <span className="w-12 shrink-0 text-right text-xs font-semibold tabular-nums text-content-primary">
            {soVN(m.value, locale)}
          </span>
        </li>
      ))}
    </ul>
  );
}
